import { useState } from 'react';
import {
    Box,
    ButtonBase,
    CircularProgress,
    Divider,
    Popover,
    Skeleton,
    Typography,
    alpha
} from '@mui/material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../hooks/useTranslate';
import useArchivosGeneradosHoy from '../hooks/useArchivosGeneradosHoy';
import type { TipoContadorArchivos } from '../consts/types';

export const ARCHIVOS_GENERADOS_HOY_TEST_IDS = {
    boton: 'archivos-generados-hoy-boton',
    cantidad: 'archivos-generados-hoy-cantidad',
    cargando: 'archivos-generados-hoy-cargando',
    desglose: 'archivos-generados-hoy-desglose',
    total: 'archivos-generados-hoy-total',
    fila: (idOrganizacion: number) => `archivos-generados-hoy-cliente-${idOrganizacion}`
};

const ETIQUETA_POR_TIPO: Record<TipoContadorArchivos, string> = {
    visitas: 'generatedFilesToday.visits',
    rgrl: 'generatedFilesToday.rgrl',
    roam: 'generatedFilesToday.roam'
};

const SIN_DATO = '—';

/** Archivos SRT que se pueden generar por día: en el desglose cada cliente se lee "X de 9". */
export const MAX_ARCHIVOS_DIARIOS = 9;

const COLUMNAS_DESGLOSE = 'minmax(0, 1fr) 56px';

const mostrar = (cantidad: number | null) => (cantidad === null ? SIN_DATO : cantidad);

/** Número dentro del botón, como los badges de PresentacionesSVCC. */
const Contador = ({ valor, cargando }: { valor: number | null; cargando: boolean }) => (
    <Box
        component="span"
        data-testid={ARCHIVOS_GENERADOS_HOY_TEST_IDS.cantidad}
        sx={{
            minWidth: 20,
            height: 20,
            px: 0.75,
            borderRadius: '999px',
            fontSize: 11,
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#EEF2F3',
            color: '#3D4A50'
        }}
    >
        {cargando ? (
            <CircularProgress size={10} data-testid={ARCHIVOS_GENERADOS_HOY_TEST_IDS.cargando} />
        ) : (
            mostrar(valor)
        )}
    </Box>
);

const Separador = () => (
    <Box component="span" sx={{ color: 'text.secondary' }}>
        ·
    </Box>
);

interface ArchivosGeneradosHoyProps {
    /** Flujo de la tab activa: es el único que se muestra. */
    tipo: TipoContadorArchivos;
    /** Cliente elegido en la tab; `null` = todos. */
    idOrganizacion: number | null;
}

/**
 * Indicador "Archivos generados hoy" del header de Gestión de Archivos SRT.
 * Muestra cuántos archivos del flujo de la tab (Visitas, RGRL/RRL o ROAM) se
 * generaron en el día (del Cliente elegido, o de todos si no hay uno) y, al
 * hacer click, el desglose por cliente con una fila de total. Cada cliente se
 * muestra sobre el máximo diario ("X/9"); el botón y el total, solo el número.
 */
export const ArchivosGeneradosHoy = ({ tipo, idOrganizacion }: ArchivosGeneradosHoyProps) => {
    const { t } = useTranslation('filesGenerationSRT');
    const { translate } = useTranslate(t);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const { cantidad, filas, idOrganizacionSeleccionada, isLoading, isError } =
        useArchivosGeneradosHoy({ tipo, idOrganizacion });

    const abierto = Boolean(anchorEl);
    const totalDesglose = filas.reduce((total, fila) => total + fila.cantidad, 0);
    const etiqueta = translate(ETIQUETA_POR_TIPO[tipo]);

    const renderDesglose = () => {
        if (isLoading) {
            return (
                <Box px={2} py={1.5}>
                    <Skeleton height={24} />
                    <Skeleton height={24} />
                </Box>
            );
        }
        if (isError) {
            return (
                <Typography px={2} py={1.5} fontSize={13} color="text.secondary">
                    {translate('generatedFilesToday.error')}
                </Typography>
            );
        }
        if (filas.length === 0) {
            return (
                <Typography px={2} py={1.5} fontSize={13} color="text.secondary">
                    {translate('generatedFilesToday.noClients')}
                </Typography>
            );
        }
        return (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {filas.map((fila) => {
                    const seleccionado = fila.idOrganizacion === idOrganizacionSeleccionada;
                    return (
                        <Box
                            component="li"
                            key={fila.idOrganizacion}
                            data-testid={ARCHIVOS_GENERADOS_HOY_TEST_IDS.fila(fila.idOrganizacion)}
                            aria-current={seleccionado ? 'true' : undefined}
                            title={
                                seleccionado
                                    ? translate('generatedFilesToday.selectedClient')
                                    : undefined
                            }
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: COLUMNAS_DESGLOSE,
                                alignItems: 'center',
                                gap: 1,
                                px: 2,
                                py: 1,
                                fontSize: 13,
                                fontWeight: seleccionado ? 700 : 400,
                                color: seleccionado ? 'primary.main' : '#3D4A50',
                                bgcolor: (theme) =>
                                    seleccionado
                                        ? alpha(theme.palette.primary.main, 0.08)
                                        : 'transparent'
                            }}
                        >
                            {/* Los nombres largos (autoseguros) pasan a otra línea */}
                            <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
                                {fila.nombre}
                            </Box>
                            <Box component="span" textAlign="right">
                                {`${fila.cantidad}/${MAX_ARCHIVOS_DIARIOS}`}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <>
            <ButtonBase
                data-testid={ARCHIVOS_GENERADOS_HOY_TEST_IDS.boton}
                aria-haspopup="dialog"
                aria-expanded={abierto}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{
                    height: 32,
                    px: 1.5,
                    // Alineado con el borde derecho de los botones del header
                    // (IconHeader deja 8px de margen a la derecha).
                    mr: 1,
                    gap: 0.75,
                    borderRadius: '999px',
                    border: '1px solid #DDE3E5',
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    color: '#3D4A50',
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#F3F8F8' }
                }}
            >
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                {translate('generatedFilesToday.title')}
                <Separador />
                {etiqueta}
                <Contador valor={cantidad} cargando={isLoading} />
            </ButtonBase>
            <Popover
                open={abierto}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: { sx: { mt: 0.5, width: 420, maxWidth: 'calc(100vw - 32px)' } }
                }}
            >
                <Box data-testid={ARCHIVOS_GENERADOS_HOY_TEST_IDS.desglose} role="dialog">
                    <Box px={2} pt={1.5} pb={1}>
                        <Typography fontSize={14} fontWeight={700} color="#3D4A50">
                            {translate('generatedFilesToday.title')}
                        </Typography>
                        <Typography fontSize={12} color="text.secondary">
                            {translate('generatedFilesToday.breakdownTitle')}
                        </Typography>
                    </Box>
                    <Divider />
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: COLUMNAS_DESGLOSE,
                            gap: 1,
                            px: 2,
                            py: 0.75,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'text.secondary'
                        }}
                    >
                        <span>{translate('generatedFilesToday.client')}</span>
                        <Box component="span" textAlign="right">
                            {etiqueta}
                        </Box>
                    </Box>
                    <Divider />
                    <Box sx={{ maxHeight: 320, overflowY: 'auto', pb: 0.5 }}>
                        {renderDesglose()}
                    </Box>
                    {!isLoading && !isError && filas.length > 0 && (
                        <>
                            <Divider />
                            <Box
                                data-testid={ARCHIVOS_GENERADOS_HOY_TEST_IDS.total}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: COLUMNAS_DESGLOSE,
                                    gap: 1,
                                    px: 2,
                                    py: 1,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: '#3D4A50'
                                }}
                            >
                                <span>{translate('generatedFilesToday.total')}</span>
                                <Box component="span" textAlign="right">
                                    {totalDesglose}
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            </Popover>
        </>
    );
};

export default ArchivosGeneradosHoy;
